/**
 * A dish as one line, with its choices behind a sheet.
 *
 * The row carries the dish, its description and its price, and one control opens
 * `MenuItemSheet`. It replaced a row that drew every group of every dish into
 * the list itself, which a dish with two groups of fifteen choices made
 * unreadable.
 *
 * No photograph, even where the merchant uploaded one: the menu reads as a list
 * of dishes rather than a gallery, and one dish with a picture among ninety
 * without it is a row that starts at the wrong edge.
 *
 * The rule, deliberately one rule: **a dish with any group opens the sheet, a
 * dish with no groups is added outright.** It costs a second tap on a dish whose
 * groups are all optional — there are some — and buys a `+` that never has to be
 * explained.
 */

import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { Plus } from 'lucide-react-native';
import {
  formatDayTime,
  formatMoney,
  minutesToTime,
  optionPriceRange,
  parseTimeToMinutes,
  weekdayName,
  type CartOption,
  type PublicMenuItem,
} from '@yummy/shared';

import { Icon, Price, Stack, Text, ToneBadge, cn, useLocale, useT } from '@yummy/ui-rnr';

import { useCart } from '../app/cart';
import { MenuItemSheet } from './MenuItemSheet';

/**
 * When a dish that is off the menu right now comes back.
 *
 * The soonest window rather than all of them: the full schedule is a merchant
 * concern, not something to parse while ordering lunch.
 */
function AvailabilityNote({ item }: { item: PublicMenuItem }) {
  const { t, locale } = useLocale();
  const [first] = item.availability_windows ?? [];

  if (!first) {
    return <ToneBadge label={t('customer.menu.unavailableNow')} tone="neutral" size="sm" />;
  }

  const start = parseTimeToMinutes(first.start_time);
  const end = parseTimeToMinutes(first.end_time);

  return (
    <Text className="text-muted-foreground text-sm">
      {t('customer.menu.availableFrom', {
        day: weekdayName(first.weekday, true, locale),
        from: start === null ? '' : minutesToTime(start),
        to: end === null ? '' : minutesToTime(end),
      })}
    </Text>
  );
}

export interface MenuItemRowProps {
  item: PublicMenuItem;
  currency: string;
  timezone: string;
  /** Adds the dish, with its choices, `quantity` times. */
  onAdd: (options: CartOption[], quantity: number) => void;
}

/**
 * Renders one menu item as a row that opens its choices in a sheet.
 *
 * @param props The dish, its currency, its timezone and the add handler.
 * @returns The row, and the sheet it opens.
 */
export function MenuItemRow({ item, currency, timezone, onAdd }: MenuItemRowProps) {
  const t = useT();
  const { lines: cartLines } = useCart();
  const [open, setOpen] = useState(false);

  const groups = item.option_groups ?? [];
  const orderable = !item.is_sold_out && item.is_available_now;

  /**
   * How many of this dish are in the order, in every combination.
   *
   * The count is the dish's, not a configuration's: a table that ordered two
   * small and one large sees `3`, because that is what the row is about. Which
   * three, and changing them, belongs to Review order.
   */
  const inOrder = cartLines
    .filter((line) => line.itemId === item.id)
    .reduce((sum, line) => sum + line.quantity, 0);

  const discounted = item.discount_cents > 0;
  const priceRange = optionPriceRange(item);

  return (
    <>
      <View className={cn('flex-row items-center gap-3', !orderable && 'opacity-55')}>
        <Stack gap="xs" className="min-w-0 flex-1">
          {/* 16 for both the dish and its price, which is what the cart and
              the order screen set a line in: a dish name is content on every
              screen, not a heading on this one. At 18 and 16 the list shouted
              over the sheet it opens, and at 16/700 it read as its own category
              heading — which is the line above it. */}
          <Text numberOfLines={2} className="text-base font-semibold">
            {item.name}
          </Text>

          {item.description ? (
            <Text numberOfLines={2} className="text-muted-foreground text-sm">
              {item.description}
            </Text>
          ) : null}

          <View className="flex-row items-center gap-2">
            {priceRange.max > priceRange.min ? (
              <Text className="text-base font-semibold tabular-nums">
                {`${formatMoney(priceRange.min, currency)} – ${formatMoney(priceRange.max, currency)}`}
              </Text>
            ) : (
              <Price
                variant="caption"
                cents={item.effective_price_cents}
                currency={currency}
                compareAtCents={discounted ? item.price_cents : null}
              />
            )}
            {discounted ? (
              <ToneBadge
                label={item.discount_label ?? t('customer.menu.onOffer')}
                tone="brand"
                emphasis="solid"
                size="sm"
              />
            ) : null}
          </View>

          {discounted && item.discount_ends_at ? (
            <Text className="text-primary-strong text-xs">
              {t('customer.menu.offerEnds', {
                when: formatDayTime(item.discount_ends_at, timezone, undefined, t),
              })}
            </Text>
          ) : null}

          {item.is_sold_out ? (
            <ToneBadge label={t('customer.menu.soldOut')} tone="danger" size="sm" />
          ) : !item.is_available_now ? (
            <AvailabilityNote item={item} />
          ) : item.track_inventory && item.inventory_left !== null && item.inventory_left <= 5 ? (
            <Text className="text-warning text-xs">
              {t('customer.menu.onlyLeft', { count: item.inventory_left })}
            </Text>
          ) : null}

          {orderable && item.min_quantity > 1 ? (
            <Text className="text-muted-foreground text-xs">
              {t('customer.menu.minimumOrder', { count: item.min_quantity })}
            </Text>
          ) : null}
        </Stack>

        {orderable ? (
          <Pressable
            onPress={() => {
              // The rule: anything with a choice to make opens the sheet;
              // anything without goes straight in.
              if (groups.length > 0) setOpen(true);
              else onAdd([], Math.max(1, item.min_quantity));
            }}
            accessibilityRole="button"
            accessibilityLabel={t('customer.menu.addItem', { item: item.name })}
            className="bg-primary-soft h-11 w-11 flex-none items-center justify-center rounded-xl active:opacity-80">
            <Icon as={Plus} size={22} className="text-primary" />
            {inOrder > 0 ? (
              <View className="bg-primary absolute -top-1.5 -right-1.5 h-5 min-w-5 items-center justify-center rounded-full px-1">
                <Text className="text-primary-foreground text-xs font-bold">{inOrder}</Text>
              </View>
            ) : null}
          </Pressable>
        ) : null}
      </View>

      {groups.length > 0 ? (
        <MenuItemSheet
          item={item}
          currency={currency}
          visible={open}
          onClose={() => setOpen(false)}
          onAdd={onAdd}
        />
      ) : null}
    </>
  );
}
